import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, 
  Download, 
  Loader2,
  Image as ImageIcon,
  Briefcase,
  Layout,
  Linkedin,
  Instagram,
  FileText,
  BookOpen,
  Award,
  X,
  RefreshCw,
  History,
  ChevronLeft,
  Wand2,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthToken } from '../App';

// Icon mapping for image types
const TYPE_ICONS = {
  hiring_poster: Briefcase,
  recruitment_banner: Layout,
  linkedin_post: Linkedin,
  instagram_post: Instagram,
  job_ad: FileText,
  course_thumbnail: BookOpen,
  certificate: Award,
  company_banner: ImageIcon,
  custom: Sparkles
};

const AIImageGenerator = ({ isOpen, onClose }) => {
  const [imageTypes, setImageTypes] = useState([]);
  const [styles, setStyles] = useState([]);
  const [selectedType, setSelectedType] = useState('hiring_poster');
  const [selectedStyle, setSelectedStyle] = useState('professional');
  const [jobTitle, setJobTitle] = useState('');
  const [details, setDetails] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [imageHistory, setImageHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Text overlay options
  const [addTextOverlay, setAddTextOverlay] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [tagline, setTagline] = useState('WE ARE HIRING');
  const [ctaText, setCtaText] = useState('APPLY NOW');

  // Load image types on mount
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API}/ai/image/types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setImageTypes(response.data.types || []);
        setStyles(response.data.styles || []);
      } catch (error) {
        console.error('Failed to load image types:', error);
      }
    };
    
    if (isOpen) {
      loadTypes();
    }
  }, [isOpen]);

  // Load history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/ai/image/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImageHistory(response.data.images || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Generate image
  const generateImage = async () => {
    if (!jobTitle.trim() && selectedType !== 'custom') {
      toast.error('Please enter a job title or topic');
      return;
    }
    
    if (selectedType === 'custom' && !customPrompt.trim()) {
      toast.error('Please enter a custom prompt');
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);

    try {
      const token = getAuthToken();
      const response = await axios.post(`${API}/ai/image/generate`, {
        image_type: selectedType,
        job_title: jobTitle,
        details: details,
        custom_prompt: selectedType === 'custom' ? customPrompt : null,
        style: selectedStyle,
        add_text_overlay: addTextOverlay,
        company_name: companyName,
        tagline: tagline,
        cta_text: ctaText
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000 // 2 minute timeout for image generation
      });

      if (response.data.success) {
        setGeneratedImage(response.data);
        toast.success('Image generated successfully!');
      } else {
        toast.error(response.data.error || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download image
  const downloadImage = (format = 'png') => {
    if (!generatedImage?.image_base64) return;

    const link = document.createElement('a');
    link.href = `data:image/${format};base64,${generatedImage.image_base64}`;
    link.download = `${selectedType}_${jobTitle.replace(/\s+/g, '_') || 'image'}_${Date.now()}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Image downloaded as ${format.toUpperCase()}`);
  };

  // Get icon for type
  const getTypeIcon = (typeId) => {
    const IconComponent = TYPE_ICONS[typeId] || Sparkles;
    return <IconComponent className="w-5 h-5" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="ai-image-generator">
      <div className="w-full max-w-5xl h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-violet-600 to-purple-600">
          <div className="flex items-center gap-3">
            {showHistory && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHistory(false)}
                className="text-white hover:bg-white/20 h-8 w-8"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            )}
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white text-lg">
                {showHistory ? 'Generation History' : 'AI Image Generator'}
              </h2>
              <p className="text-sm text-white/70">
                {showHistory ? 'Your recently generated images' : 'Create visual backgrounds & templates'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!showHistory && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { loadHistory(); setShowHistory(true); }}
                className="text-white hover:bg-white/20 h-9 w-9"
                title="History"
              >
                <History className="w-5 h-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20 h-9 w-9"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {showHistory ? (
          /* History View */
          <ScrollArea className="flex-1 p-6">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
              </div>
            ) : imageHistory.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No images generated yet</p>
                <Button
                  variant="outline"
                  onClick={() => setShowHistory(false)}
                  className="mt-4"
                >
                  Create your first image
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {imageHistory.map((img, idx) => (
                  <div key={idx} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {img.image_type}
                      </Badge>
                    </div>
                    <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                      {img.job_title || img.title || 'Custom Image'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(img.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        ) : (
          /* Generator View */
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - Options */}
            <div className="w-80 border-r border-slate-200 dark:border-slate-700 flex flex-col">
              <ScrollArea className="flex-1 p-4">
                {/* Image Type Selection */}
                <div className="mb-6">
                  <Label className="text-sm font-medium mb-3 block">Image Type</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {imageTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setSelectedType(type.id)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          selectedType === type.id
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-violet-300'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                          selectedType === type.id
                            ? 'bg-violet-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}>
                          {getTypeIcon(type.id)}
                        </div>
                        <p className="text-xs font-medium text-slate-900 dark:text-white truncate">
                          {type.name}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Style Selection */}
                <div className="mb-6">
                  <Label className="text-sm font-medium mb-3 block">Style</Label>
                  <div className="flex flex-wrap gap-2">
                    {styles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setSelectedStyle(style.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedStyle === style.id
                            ? 'bg-violet-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                        }`}
                      >
                        {style.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Input Fields */}
                <div className="space-y-4">
                  {selectedType !== 'custom' ? (
                    <>
                      <div>
                        <Label htmlFor="jobTitle" className="text-sm font-medium mb-2 block">
                          Job Title / Topic *
                        </Label>
                        <Input
                          id="jobTitle"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          placeholder="e.g., AML Investigator, Software Engineer"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Label htmlFor="details" className="text-sm font-medium mb-2 block">
                          Additional Details
                        </Label>
                        <Textarea
                          id="details"
                          value={details}
                          onChange={(e) => setDetails(e.target.value)}
                          placeholder="Location: Mumbai, Salary: 5-6 LPA, Experience: 2+ years"
                          rows={2}
                          className="w-full"
                        />
                      </div>
                      
                      {/* Text Overlay Options */}
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Add Text Overlay
                          </Label>
                          <button
                            onClick={() => setAddTextOverlay(!addTextOverlay)}
                            className={`w-10 h-6 rounded-full transition-colors relative ${
                              addTextOverlay ? 'bg-violet-500' : 'bg-slate-300'
                            }`}
                          >
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                              addTextOverlay ? 'left-5' : 'left-1'
                            }`} />
                          </button>
                        </div>
                        
                        {addTextOverlay && (
                          <div className="space-y-3 mt-3">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-slate-500 mb-1 block">Tagline</Label>
                                <Input
                                  value={tagline}
                                  onChange={(e) => setTagline(e.target.value)}
                                  placeholder="WE ARE HIRING"
                                  className="text-sm h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500 mb-1 block">CTA Button</Label>
                                <Input
                                  value={ctaText}
                                  onChange={(e) => setCtaText(e.target.value)}
                                  placeholder="APPLY NOW"
                                  className="text-sm h-8"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500 mb-1 block">Company Name (optional)</Label>
                              <Input
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="Your Company Name"
                                className="text-sm h-8"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label htmlFor="customPrompt" className="text-sm font-medium mb-2 block">
                        Custom Prompt *
                      </Label>
                      <Textarea
                        id="customPrompt"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Describe the image you want to generate in detail..."
                        rows={5}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Generate Button */}
              <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                <Button
                  onClick={generateImage}
                  disabled={isGenerating}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white gap-2"
                  data-testid="generate-image-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating... (up to 60s)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Image
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Right Panel - Preview */}
            <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-800/50">
              <div className="flex-1 flex items-center justify-center p-6">
                {isGenerating ? (
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 mx-auto flex items-center justify-center mb-4 animate-pulse">
                      <Wand2 className="w-10 h-10 text-white" />
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 font-medium">
                      Creating your image...
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      This may take up to 60 seconds
                    </p>
                    <div className="mt-4 flex justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                    </div>
                  </div>
                ) : generatedImage ? (
                  <div className="max-w-full max-h-full">
                    <img
                      src={`data:image/png;base64,${generatedImage.image_base64}`}
                      alt="Generated"
                      className="max-w-full max-h-[60vh] rounded-xl shadow-2xl object-contain"
                      data-testid="generated-image"
                    />
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-2xl bg-slate-200 dark:bg-slate-700 mx-auto flex items-center justify-center mb-4">
                      <ImageIcon className="w-12 h-12 text-slate-400" />
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 font-medium">
                      Your image will appear here
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Select options and click Generate
                    </p>
                    <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 max-w-sm mx-auto">
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        <strong>Tip:</strong> AI generates visual backgrounds without text. Add your job title and details as text overlay using Canva or similar tools after downloading.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Download Actions */}
              {generatedImage && (
                <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {generatedImage.image_type}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          Generated successfully
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setGeneratedImage(null);
                            generateImage();
                          }}
                          className="gap-1"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Regenerate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadImage('png')}
                          className="gap-1"
                        >
                          <Download className="w-4 h-4" />
                          PNG
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => downloadImage('jpg')}
                          className="gap-1 bg-violet-600 hover:bg-violet-700 text-white"
                        >
                          <Download className="w-4 h-4" />
                          JPG
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                      Download and add your text using Canva, Photoshop, or any design tool for best results
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIImageGenerator;
